import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, Copy, RotateCw } from 'lucide-react';
import { reportError } from '../../lib/errorLog';

// Catches render-time crashes anywhere below it so one broken component does not
// blank the whole app. componentDidCatch forwards the error to the same sink
// every other failure uses (POST /errors + the toast surface), then we show a
// calm, tactile fallback the user can act on: reload, or copy the details to
// hand back. A class component is the only way to get componentDidCatch; there
// is no hook for it. We never wrap hooks in try/catch (that breaks the Rules of
// Hooks and triggers React #310). The boundary is the right tool here.

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  copied: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError({
      severity: 'fatal',
      message: error.message || 'Render crash',
      stack: error.stack ?? info.componentStack ?? undefined,
      context: {
        page: typeof location !== 'undefined' ? location.pathname : '',
      },
    });
  }

  private handleReload = (): void => {
    if (typeof location !== 'undefined') location.reload();
  };

  private handleCopy = (): void => {
    const { error } = this.state;
    if (!error) return;
    const page = typeof location !== 'undefined' ? location.pathname : '';
    const detail = [
      `Page: ${page}`,
      `Message: ${error.message}`,
      error.stack ? `Stack:\n${error.stack}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
    void navigator.clipboard
      ?.writeText(detail)
      .then(() => {
        this.setState({ copied: true });
        window.setTimeout(() => this.setState({ copied: false }), 2000);
      })
      .catch(() => {
        /* clipboard can fail silently; the toast already logged the crash */
      });
  };

  render(): ReactNode {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-[60vh] place-items-center px-4 py-12">
        <div className="card-tactile w-full max-w-md p-6">
          <div className="mb-3 flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-signal-amber/15 text-signal-amber">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink-900">Something broke on this screen</h2>
              <p className="text-xs text-ink-500">It has been logged. Your saved work is safe.</p>
            </div>
          </div>
          <p className="mb-4 rounded-xl bg-ink-50 px-3 py-2 text-sm text-ink-700">{error.message}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm text-white hover:bg-brand-700 active:scale-[0.99]"
            >
              <RotateCw className="h-4 w-4" /> Reload
            </button>
            <button
              type="button"
              onClick={this.handleCopy}
              className="inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm hover:bg-ink-50 active:scale-[0.99]"
            >
              <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy details'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
